// src/components/forms/AppForm.tsx

import { Form, Input, Select, Button, Radio, Card, Tooltip } from 'antd';
import { useState, useEffect } from 'react';
import { 
  AppstoreOutlined, 
  AppleOutlined, 
  AndroidOutlined, 
  SettingOutlined, 
  QuestionCircleOutlined 
} from '@ant-design/icons';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import type { CreateAppRequest } from '@/types';

/**
 * App 创建表单组件
 */
export const AppForm = ({
  onSubmit,
}: {
  onSubmit: (values: CreateAppRequest) => Promise<void>;
}) => {
  const [form] = Form.useForm<CreateAppRequest>();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [autoChannel, setAutoChannel] = useState<boolean>(true);

  /**
   * 处理表单提交
   */
  const onFinish = async (values: CreateAppRequest): Promise<void> => {
    setSubmitting(true);
    setError(null);

    try {
      await onSubmit(values);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '创建失败';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // SDK 类别选项
  const sdkCategoryOptions = [
    { value: 'smart_home', label: '智能生活', icon: <SettingOutlined /> },
    // { value: 'wearable', label: '可穿戴设备', icon: <SettingOutlined /> },
    // { value: 'healthcare', label: '健康医疗', icon: <SettingOutlined /> },
    // { value: 'industrial', label: '工业 IoT', icon: <SettingOutlined /> },
  ];

  // 渠道标识符自动生成逻辑
  const autoGeneratedChannel = Form.useWatch('android_package_name', form);
  const generatedChannel = autoGeneratedChannel 
    ? autoGeneratedChannel.split('.').slice(1).join('') || autoGeneratedChannel
    : '';

  // 监听 Android 包名变化，自动更新渠道标识符
  useEffect(() => {
    if (autoChannel && generatedChannel) {
      form.setFieldsValue({ channel: generatedChannel });
    }
  }, [autoChannel, generatedChannel, form]);

  return (
    <div className="max-w-4xl mx-auto">
      {/* 标题区域 */}
      <div className="mb-10 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-linear-to-r from-blue-500 to-purple-600 rounded-full mb-6">
          <AppstoreOutlined className="text-white text-2xl" />
        </div>
        <h2 className="text-2xl font-bold text-gray-800 mb-3">创建 App SDK</h2>
        <p className="text-gray-600 max-w-2xl mx-auto">
          为您的应用程序配置 SDK 以接入 IoT 平台
        </p>
      </div>
      
      {/* 错误提示 */}
      {error && (
        <div className="mb-8">
          <ErrorAlert 
            message="创建失败" 
            description={error} 
          />
        </div>
      )}

      {/* 表单卡片 */}
      <Card 
        className="shadow-lg border-0 rounded-xl"
        styles={{ body: { padding: '32px' } }}
      >
        <Form
          form={form}
          name="app"
          layout="vertical"
          onFinish={onFinish}
          requiredMark={false}
          className="space-y-8"
        >
          {/* ========== 基本信息 ========== */}
          <div className="space-y-6">
            <div className="flex items-center space-x-3 mb-5">
              <div className="w-2 h-7 bg-blue-500 rounded"></div>
              <h3 className="text-lg font-semibold text-gray-800">基本信息</h3>
            </div>
            
            <Form.Item
              name="name"
              label={
                <span className="font-medium text-gray-700 flex items-center">
                  App 名称
                  <span className="text-red-500 ml-1">*</span>
                  <Tooltip title="请为您的应用起一个易于识别的名称">
                    <QuestionCircleOutlined className="ml-2 text-gray-400 text-sm" />
                  </Tooltip>
                </span>
              }
              rules={[{ required: true, message: '请输入 App 名称' }]}
              className="mb-0"
            >
              <Input 
                placeholder="例如：智能家居控制" 
                size="large"
                disabled={submitting}
                className="rounded-lg border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-colors"
              />
            </Form.Item>
          </div>

          {/* ========== 平台配置 ========== */}
          <div className="space-y-6">
            <div className="flex items-center space-x-3 mb-5">
              <div className="w-2 h-7 bg-green-500 rounded"></div>
              <h3 className="text-lg font-semibold text-gray-800">平台配置</h3>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-7">
              <Form.Item
                name="ios_bundle_id"
                label={
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <AppleOutlined className="text-blue-600 text-xl" />
                    </div>
                    <span className="font-medium text-gray-700">
                      iOS Bundle ID
                      <span className="text-red-500 ml-1">*</span>
                      <Tooltip title="在 Xcode 项目的 General → Identity → Bundle Identifier 中找到">
                        <QuestionCircleOutlined className="ml-2 text-gray-400 text-sm" />
                      </Tooltip>
                    </span>
                  </div>
                }
                rules={[
                  { required: true, message: '请输入 iOS Bundle ID' },
                  { 
                    pattern: /^[a-zA-Z][a-zA-Z0-9\-.]*$/,
                    message: 'Bundle ID 格式不正确，例如 com.yourcompany.appname。'
                  }
                ]}
                className="mb-0"
              >
                <Input 
                  placeholder="com.example.iosapp" 
                  size="large"
                  disabled={submitting}
                  // prefix={<AppleOutlined className="text-gray-400" />}
                  className="rounded-lg border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-colors"
                />
              </Form.Item>

              <Form.Item
                name="android_package_name"
                label={
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <AndroidOutlined className="text-green-600 text-xl" />
                    </div>
                    <span className="font-medium text-gray-700">
                      Android 包名
                      <span className="text-red-500 ml-1">*</span>
                      <Tooltip title="在 AndroidManifest.xml 文件的 package 属性中找到">
                        <QuestionCircleOutlined className="ml-2 text-gray-400 text-sm" />
                      </Tooltip>
                    </span>
                  </div>
                }
                rules={[
                  { required: true, message: '请输入 Android 包名' },
                  { 
                    pattern: /^[a-zA-Z][a-zA-Z0-9_]*(\.[a-zA-Z][a-zA-Z0-9_]*)*$/,
                    message: '包名格式不正确，例如 com.yourcompany.appname。'
                  }
                ]}
                className="mb-0"
              >
                <Input 
                  placeholder="com.example.androidapp" 
                  size="large"
                  disabled={submitting}
                  // prefix={<AndroidOutlined className="text-gray-400" />}
                  className="rounded-lg border-gray-300 focus:border-green-500 focus:ring-2 focus:ring-green-100 transition-colors"
                  onChange={(e) => {
                    if (autoChannel) {
                      const packageName = e.target.value;
                      const newChannel = packageName.split('.').slice(1).join('') || packageName;
                      form.setFieldsValue({ channel: newChannel });
                    }
                  }}
                />
              </Form.Item>
            </div>
          </div>

          {/* <Divider className="my-10" /> */}

          {/* ========== 高级设置 ========== */}
          <div className="space-y-6">
            <div className="flex items-center space-x-3 mb-5">
              <div className="w-2 h-7 bg-purple-500 rounded"></div>
              <h3 className="text-lg font-semibold text-gray-800">高级设置</h3>
            </div>
            
            {/* SDK 类别 - 单独一行 */}
            <Form.Item
              name="sdk_category"
              label={
                <span className="font-medium text-gray-700 flex items-center">
                  SDK 类别
                  <span className="text-red-500 ml-1">*</span>
                  <Tooltip title="选择您的应用所属的业务类别">
                    <QuestionCircleOutlined className="ml-2 text-gray-400 text-sm" />
                  </Tooltip>
                </span>
              }
              rules={[{ required: true, message: '请选择 SDK 类别' }]}
              className="mb-0"
            >
              <Select
                placeholder="选择类别"
                size="large"
                disabled={submitting}
                options={sdkCategoryOptions.map(option => ({
                  ...option,
                  label: (
                    <div className="flex items-center space-x-2.5">
                      {/* <div className="w-6 h-6 bg-gray-200 rounded flex items-center justify-center text-gray-700">
                        {option.icon}
                      </div> */}
                      <span>{option.label}</span>
                    </div>
                  )
                }))}
                className="rounded-lg"
              />
            </Form.Item>

            {/* 渠道标识符 - 独立成行，简化说明 */}
            <Form.Item
              label={
                <span className="font-medium text-gray-700 flex items-center">
                  渠道标识符
                  <span className="text-red-500 ml-1">*</span>
                  <Tooltip title="用于区分不同渠道的应用版本">
                    <QuestionCircleOutlined className="ml-2 text-gray-400 text-sm" />
                  </Tooltip>
                </span>
              }
              className="mb-0"
            >
              <div className="space-y-4">
                {/* 简化说明 */}
                {/* <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                  <QuestionCircleOutlined className="mr-2 text-blue-600" />
                  选择生成方式：系统支持自动生成（推荐）或手动输入
                </div> */}
                
                {/* 单选按钮组 */}
                <Radio.Group 
                  value={autoChannel ? 'auto' : 'manual'} 
                  onChange={(e) => setAutoChannel(e.target.value === 'auto')}
                  disabled={submitting}
                  className="w-full"
                >
                  <div className="flex flex-col md:flex-row gap-3">
                    {/* 自动模式 */}
                    <Radio 
                      value="auto" 
                      className={`flex-1 min-w-35 p-0 ${
                        autoChannel 
                          ? 'border-2 border-blue-500 bg-blue-50' 
                          : 'border border-gray-200 hover:border-blue-400'
                      } rounded-lg transition-all duration-200`}
                      style={{ paddingLeft: '10px' }}
                    >
                      <div className="p-3 w-full text-left">
                        <div className="font-medium text-gray-800 mb-1 flex items-center">
                          {/* <span className={`inline-block w-2.5 h-2.5 rounded-full mr-3 ${
                            autoChannel ? 'bg-blue-500' : 'bg-gray-400'
                          }`}></span> */}
                          自动生成
                        </div>
                        <div className="text-xs text-gray-600">
                          基于 Android 包名
                        </div>
                      </div>
                    </Radio>
                    
                    {/* 手动模式 - 改为蓝色主题 */}
                    <Radio 
                      value="manual" 
                      className={`flex-1 min-w-35 p-0 ${
                        !autoChannel 
                          ? 'border-2 border-blue-500 bg-blue-50' 
                          : 'border border-gray-200 hover:border-blue-400'
                      } rounded-lg transition-all duration-200`}
                      style={{ paddingLeft: '10px' }}
                    >
                      <div className="p-3 w-full text-left">
                        <div className="font-medium text-gray-800 mb-1 flex items-center">
                          {/* <span className={`inline-block w-2.5 h-2.5 rounded-full mr-3 ${
                            !autoChannel ? 'bg-blue-500' : 'bg-gray-400'
                          }`}></span> */}
                          手动输入
                        </div>
                        <div className="text-xs text-gray-600">
                          自定义标识符
                        </div>
                      </div>
                    </Radio>
                  </div>
                </Radio.Group>

                <br />
                <br />
                
                {/* 渠道标识符输入 */}
                <div>
                  {autoChannel ? (
                    <Form.Item
                      name="channel"
                      noStyle
                      rules={[{ required: true, message: '请填写渠道标识符' }]}
                    >
                      <Input 
                        placeholder="系统将根据 Android 包名自动生成" 
                        size="large"
                        disabled={submitting}
                        value={generatedChannel}
                        readOnly
                        className="rounded-lg border-gray-300 bg-gray-100"
                      />
                      {/* <div className="mt-2 text-xs text-gray-600">
                        <QuestionCircleOutlined className="mr-2 text-blue-500" />
                        已根据 Android 包名生成：{autoGeneratedChannel || '未填写'}
                      </div> */}
                    </Form.Item>
                  ) : (
                    <Form.Item
                      name="channel"
                      noStyle
                      rules={[
                        { required: true, message: '请填写渠道标识符' },
                        { 
                          pattern: /^[a-zA-Z0-9_-]+$/,
                          message: '只能包含字母、数字、下划线和横线'
                        }
                      ]}
                    >
                      <Input 
                        placeholder="唯一标识，不可更改。不填写将根据包名自动生成" 
                        size="large"
                        disabled={submitting}
                        className="rounded-lg border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-colors"
                      />
                      {/* <div className="mt-2 text-xs text-gray-600">
                        <QuestionCircleOutlined className="mr-2 text-blue-500" />
                        常用示例：official（官方）、beta（测试）、enterprise（企业定制）
                      </div> */}
                    </Form.Item>
                  )}
                </div>
              </div>
            </Form.Item>
          </div>

          {/* <Divider className="my-10" /> */}

          {/* 提交按钮 */}
          <Form.Item className="mb-0 pt-6">
            <div className="flex justify-end space-x-5">
              <Button
                size="large"
                onClick={() => form.resetFields()}
                disabled={submitting}
                className="px-7 py-2.5 border-gray-300 hover:border-gray-400 hover:bg-gray-50 transition-colors rounded-lg"
              >
                重置
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={submitting}
                disabled={submitting}
                className="px-9 py-2.5 bg-linear-to-r from-blue-500 to-purple-600 border-0 hover:from-blue-600 hover:to-purple-700 text-white font-medium shadow-md hover:shadow-lg transition-all duration-200 rounded-lg"
              >
                {submitting ? '创建中...' : '创建 App SDK'}
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};